/* eslint-disable */

import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SlotAccountingGrid from "components/slotAccounting/SlotAccountingGrid";
import AddVisitTransactionButton from "components/VisitTransactionDetails/addVisitTransactionButton";

import LocalService from "services/localService";
import LocalMachineService from "services/localMachineService";
import LocalMachineProfitPercentageService from "services/localMachineProfitPercentageService";
import SlotAccountingService from "services/slotAccountingService";
import VisitMachineService from "services/visitMachineService";
import {
  debugTextButtonSx,
  filterApplyButtonSx,
  filterClearButtonSx,
  secondaryOutlinedButtonSx,
} from "components/common/buttonStyles";

import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";

const headerContainedBtnSx = {
  color: "#fff",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  backgroundColor: "rgba(255,255,255,0.14)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
};

const canShowDebugJson = process.env.NODE_ENV !== "production";
const RECENT_COLLECTIONS_LIMIT = 20;

const defaultFilters = {
  from: "",
  to: "",
  activeOnly: false,
  includeInitialValues: true,
};

const normalizeFilters = (filters = {}) => ({
  ...defaultFilters,
  ...filters,
  includeInitialValues: true,
});

const filterActiveProfit = (list) =>
  (list || []).filter((profitPercentage) => profitPercentage?.IsActive !== false);

const defaultPagination = {
  limit: RECENT_COLLECTIONS_LIMIT,
  hasMore: false,
  nextBefore: null,
  nextBeforeVisitId: null,
  mode: "recent",
};

const hasDateRangeFilters = (filters = {}) => Boolean(filters?.from || filters?.to);

const normalizeDisplayNumberHistory = (values = []) => {
  const history = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0)
    .reduce((normalizedHistory, value) => {
      if (normalizedHistory[normalizedHistory.length - 1] !== value) {
        normalizedHistory.push(value);
      }
      return normalizedHistory;
    }, []);

  for (let size = 1; size <= Math.floor(history.length / 2); size += 1) {
    if (history.length % size !== 0) continue;
    let repeats = true;
    for (let index = size; index < history.length; index += 1) {
      if (history[index] !== history[index % size]) {
        repeats = false;
        break;
      }
    }
    if (repeats) return history.slice(0, size);
  }

  return history;
};

const toPositiveIntegerOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const getMachineSortDisplayNumber = (machine = {}) => {
  const directNumber = toPositiveIntegerOrNull(machine?.localDisplayNumber ?? machine?.LocalDisplayNumber);
  if (directNumber != null) return directNumber;

  const history = normalizeDisplayNumberHistory(
    machine?.localDisplayNumberHistory || machine?.LocalDisplayNumberHistory || []
  );
  if (history.length > 0) return history[history.length - 1];

  return toPositiveIntegerOrNull(machine?.currentLocalDisplayNumber);
};

const compareMachineColumns = (a = {}, b = {}) => {
  if (a?.currentActive === true && b?.currentActive !== true) return -1;
  if (a?.currentActive !== true && b?.currentActive === true) return 1;

  const numberA = getMachineSortDisplayNumber(a);
  const numberB = getMachineSortDisplayNumber(b);
  if (numberA != null && numberB == null) return -1;
  if (numberA == null && numberB != null) return 1;
  if (numberA != null && numberB != null && numberA !== numberB) return numberA - numberB;

  const nameCompare = String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
    sensitivity: "base",
  });
  if (nameCompare !== 0) return nameCompare;

  const serialCompare = String(a?.serialNumber || "").localeCompare(String(b?.serialNumber || ""), "es", {
    sensitivity: "base",
  });
  if (serialCompare !== 0) return serialCompare;

  const machineIdA = Number(a?.machineId);
  const machineIdB = Number(b?.machineId);
  if (Number.isFinite(machineIdA) && Number.isFinite(machineIdB) && machineIdA !== machineIdB) {
    return machineIdA - machineIdB;
  }

  return String(a?.columnKey || "").localeCompare(String(b?.columnKey || ""), "es", {
    sensitivity: "base",
  });
};

const mergeDisplayNumberHistory = (olderMachine = {}, newerMachine = {}) => {
  const history = normalizeDisplayNumberHistory([
    ...(olderMachine?.localDisplayNumberHistory || olderMachine?.LocalDisplayNumberHistory || []),
    olderMachine?.localDisplayNumber ?? olderMachine?.LocalDisplayNumber,
    ...(newerMachine?.localDisplayNumberHistory || newerMachine?.LocalDisplayNumberHistory || []),
    newerMachine?.localDisplayNumber ?? newerMachine?.LocalDisplayNumber,
  ]);

  return {
    localDisplayNumberHistory: history,
    localDisplayNumber: history.length ? history[history.length - 1] : null,
    currentLocalDisplayNumber:
      newerMachine?.currentLocalDisplayNumber ??
      newerMachine?.localDisplayNumber ??
      newerMachine?.LocalDisplayNumber ??
      (history.length ? history[history.length - 1] : null),
  };
};

const buildEmptyData = (local = null, filters = defaultFilters) => ({
  local: local || null,
  filters: normalizeFilters(filters),
  machines: [],
  rows: [],
  pagination: { ...defaultPagination },
});

const mergeMachines = (currentMachines = [], nextMachines = []) => {
  const byColumnKey = new Map();
  [...currentMachines, ...nextMachines].forEach((machine) => {
    const key = String(machine?.columnKey || "").trim();
    if (!key) return;
    if (!byColumnKey.has(key)) {
      byColumnKey.set(key, machine);
      return;
    }
    const current = byColumnKey.get(key);
    const displayNumberMerge = mergeDisplayNumberHistory(machine, current);
    byColumnKey.set(key, {
      ...current,
      ...machine,
      ...displayNumberMerge,
      currentActive: machine?.currentActive ?? current?.currentActive ?? false,
    });
  });

  return Array.from(byColumnKey.values()).sort(compareMachineColumns);
};

const mergeRows = (currentRows = [], nextRows = []) => {
  const byRowKey = new Map();
  [...currentRows, ...nextRows].forEach((row) => {
    const key = String(row?.rowKey || "").trim();
    if (!key) return;
    byRowKey.set(key, row);
  });

  return Array.from(byRowKey.values()).sort((a, b) => {
    const timeA = new Date(a?.dateTime || 0).getTime();
    const timeB = new Date(b?.dateTime || 0).getTime();
    if (timeA !== timeB) return timeB - timeA;
    return Number(b?.visitId || 0) - Number(a?.visitId || 0);
  });
};

function SlotAccounting() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const localId = Number(id);
  const localFromState = location.state?.local || null;
  const restoredViewState = location.state?.viewState || null;
  const restoredSnapshot = restoredViewState?.slotAccountingSnapshot || null;
  const restoredFilters = normalizeFilters(restoredViewState?.filters);
  const hasRestoredSnapshot = Boolean(restoredSnapshot?.data);

  const [localInfo, setLocalInfo] = useState(localFromState);
  const [data, setData] = useState(() => restoredSnapshot?.data || null);
  const [pagination, setPagination] = useState(() => restoredSnapshot?.pagination || { ...defaultPagination });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterWarning, setFilterWarning] = useState("");
  const [showDebugJson, setShowDebugJson] = useState(() =>
    canShowDebugJson ? restoredViewState?.showDebugJson ?? false : false
  );
  const [skipNextAutoLoad, setSkipNextAutoLoad] = useState(hasRestoredSnapshot);

  const [filters, setFilters] = useState(() => restoredFilters);
  const [appliedFilters, setAppliedFilters] = useState(() => restoredFilters);
  const [activeLocalMachines, setActiveLocalMachines] = useState([]);
  const [profitPercentages, setProfitPercentages] = useState([]);
  const [pendingInitialSlotMachines, setPendingInitialSlotMachines] = useState([]);
  const [isLoadingCollectionPrerequisites, setIsLoadingCollectionPrerequisites] = useState(true);

  const loadLocal = useCallback(async () => {
    if (localFromState) {
      setLocalInfo(localFromState);
      return;
    }

    if (!Number.isFinite(localId)) return;

    try {
      const fetched = await LocalService.getLocalWithOwnerById(localId);
      setLocalInfo(fetched || null);
    } catch (error) {
      console.error("Error cargando local para slot accounting:", error);
      setLocalInfo(null);
    }
  }, [localFromState, localId]);

  const loadAccounting = useCallback(
    async ({
      nextFilters = defaultFilters,
      append = false,
      before = null,
      beforeVisitId = null,
    } = {}) => {
      if (!Number.isFinite(localId)) return;

      const normalizedFilters = normalizeFilters(nextFilters);
      const isRangeMode = hasDateRangeFilters(normalizedFilters);

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setErrorMessage("");

      try {
        const response = await SlotAccountingService.getSlotAccountingByLocal(localId, {
          from: normalizedFilters.from || undefined,
          to: normalizedFilters.to || undefined,
          activeOnly: normalizedFilters.activeOnly,
          includeInitialValues: true,
          limit: isRangeMode ? undefined : RECENT_COLLECTIONS_LIMIT,
          before: isRangeMode ? undefined : before,
          beforeVisitId: isRangeMode ? undefined : beforeVisitId,
        });

        const normalizedResponse = response || buildEmptyData(localInfo, normalizedFilters);
        const nextPagination = normalizedResponse?.pagination || {
          ...defaultPagination,
          mode: isRangeMode ? "range" : "recent",
          limit: isRangeMode ? null : RECENT_COLLECTIONS_LIMIT,
        };

        if (append) {
          setData((prev) => {
            const base = prev || buildEmptyData(localInfo, normalizedFilters);
            return {
              ...normalizedResponse,
              local: normalizedResponse?.local || base?.local || null,
              filters: normalizedResponse?.filters || base?.filters,
              machines: mergeMachines(base?.machines || [], normalizedResponse?.machines || []),
              rows: mergeRows(base?.rows || [], normalizedResponse?.rows || []),
            };
          });
        } else {
          setData(normalizedResponse);
        }

        setPagination(nextPagination);
      } catch (error) {
        const backendMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo cargar la contabilidad de tragamonedas.";

        setErrorMessage(backendMessage);
        if (!append) {
          setData(null);
          setPagination({ ...defaultPagination });
        }
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [localId, localInfo]
  );

  const loadCollectionPrerequisites = useCallback(async () => {
    if (!Number.isFinite(localId)) return;

    setIsLoadingCollectionPrerequisites(true);

    try {
      const [activeList, profitData, pendingInitials] = await Promise.all([
        LocalMachineService.getActiveLocalMachineByLocal(localId),
        LocalMachineProfitPercentageService.getLocalMachineProfitPercentagesByLocal(localId),
        VisitMachineService.getPendingInitialSlotMachinesByLocal(localId),
      ]);

      setActiveLocalMachines(activeList || []);
      setProfitPercentages(filterActiveProfit(profitData || []));
      setPendingInitialSlotMachines(pendingInitials || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error cargando prerequisitos de recaudación:", error);
      }
      setActiveLocalMachines([]);
      setProfitPercentages([]);
      setPendingInitialSlotMachines([]);
    } finally {
      setIsLoadingCollectionPrerequisites(false);
    }
  }, [localId]);

  useEffect(() => {
    loadLocal();
  }, [loadLocal]);

  useEffect(() => {
    if (skipNextAutoLoad) {
      setSkipNextAutoLoad(false);
      setLoading(false);
      return;
    }

    loadAccounting({ nextFilters: appliedFilters, append: false });
  }, [loadAccounting, appliedFilters, skipNextAutoLoad]);

  useEffect(() => {
    loadCollectionPrerequisites();
  }, [loadCollectionPrerequisites]);

  const handleFilterChange = (field) => (event) => {
    const nextValue =
      field === "activeOnly"
        ? event.target.checked
        : event.target.value;

    setFilterWarning("");
    setFilters((prev) => normalizeFilters({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleApplyFilters = async () => {
    if (filters.from && filters.to && filters.from > filters.to) {
      setFilterWarning("La fecha Desde no puede ser mayor que Hasta.");
      return;
    }

    setFilterWarning("");
    setAppliedFilters(normalizeFilters(filters));
  };

  const handleClearFilters = () => {
    const nextFilters = normalizeFilters(defaultFilters);
    setFilterWarning("");
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const handleLoadMore = async () => {
    if (loadingMore || !pagination?.hasMore || hasDateRangeFilters(appliedFilters)) return;

    await loadAccounting({
      nextFilters: appliedFilters,
      append: true,
      before: pagination?.nextBefore || undefined,
      beforeVisitId: pagination?.nextBeforeVisitId || undefined,
    });
  };

  const machines = data?.machines || [];
  const rows = data?.rows || [];
  const isRangeMode = hasDateRangeFilters(appliedFilters);
  const hasAppliedFilters =
    Boolean(appliedFilters.from || appliedFilters.to) ||
    Boolean(appliedFilters.activeOnly);
  const hasDraftFilters =
    Boolean(filters.from || filters.to) ||
    Boolean(filters.activeOnly);
  const filtersChanged =
    filters.from !== appliedFilters.from ||
    filters.to !== appliedFilters.to ||
    Boolean(filters.activeOnly) !== Boolean(appliedFilters.activeOnly);
  const shouldShowClearFilters = hasAppliedFilters && !filtersChanged;
  const filtersButtonLabel = shouldShowClearFilters ? "Limpiar filtros" : "Aplicar filtros";
  const isFiltersButtonDisabled = !shouldShowClearFilters && !hasDraftFilters;
  const localForActions = {
    ...(localInfo || {}),
    Id: localInfo?.Id ?? localId,
    Name: localInfo?.Name ?? data?.local?.name ?? null,
  };
  const hasAnyActiveLocalMachine = activeLocalMachines.length > 0;
  const hasPendingInitialSlotMachines = pendingInitialSlotMachines.length > 0;
  const canAddCollection = hasAnyActiveLocalMachine && !hasPendingInitialSlotMachines;
  const shouldShowAddCollectionButton = hasAnyActiveLocalMachine;
  const slotAccountingReturnTo = {
    pathname: `/local/${localId}/recaudaciones/tragamonedas`,
    label: "Volver a recaudaciones",
    viewState: {
      filters: normalizeFilters(appliedFilters),
      showDebugJson: canShowDebugJson ? showDebugJson : false,
      gridScroll: restoredViewState?.gridScroll || null,
      slotAccountingSnapshot: {
        data,
        pagination,
      },
    },
  };

  const hasNoMachines = !loading && !errorMessage && machines.length === 0;
  const hasMachinesButNoRows = !loading && !errorMessage && machines.length > 0 && rows.length === 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <MDTypography variant="h6" color="white">
                    Recaudaciones
                  </MDTypography>
                  <MDTypography variant="button" color="white" sx={{ opacity: 0.9 }}>
                    Local: {data?.local?.name ?? localInfo?.Name ?? "Cargando..."}.
                    {" "}Cliente: {data?.local?.ownerName ?? localInfo?.Person?.Name ?? "Sin dueño"}.
                  </MDTypography>
                </Box>

                <Button
                  variant="contained"
                  color="inherit"
                  disableElevation
                  sx={headerContainedBtnSx}
                  onClick={() => navigate(`/local/${id}`, { state: { local: localInfo } })}
                >
                  Volver al local
                </Button>
              </MDBox>

              <MDBox mx={2} mt={3} py={2} px={2}>
                {shouldShowAddCollectionButton && (
                  <>
                    <Box display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap">
                      <AddVisitTransactionButton
                        local={localForActions}
                        profitPercentages={profitPercentages}
                        localMachineDetails={activeLocalMachines}
                        disabled={!Number.isFinite(localId) || isLoadingCollectionPrerequisites || !canAddCollection}
                        returnTo={slotAccountingReturnTo}
                      />
                    </Box>
                    {hasPendingInitialSlotMachines && (
                      <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                        Existen tragamonedas pendientes de valores iniciales. No se puede ingresar
                        recaudación sin valores iniciales.
                      </Alert>
                    )}
                  </>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                    p: 2,
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                      gap: 2,
                      flex: "1 1 auto",
                      minWidth: 0,
                    }}
                  >
                    <TextField
                      label="Desde"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={filters.from}
                      onChange={handleFilterChange("from")}
                      sx={{ minWidth: { xs: "100%", sm: 170 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                    />
                    <TextField
                      label="Hasta"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={filters.to}
                      onChange={handleFilterChange("to")}
                      sx={{ minWidth: { xs: "100%", sm: 170 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.activeOnly}
                          onChange={handleFilterChange("activeOnly")}
                        />
                      }
                      label="Solo activas"
                      sx={{ mr: 0 }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flex: { xs: "1 1 100%", md: "0 0 auto" } }}>
                    <Button
                      variant={shouldShowClearFilters ? "outlined" : "contained"}
                      color="info"
                      disabled={isFiltersButtonDisabled}
                      sx={shouldShowClearFilters ? filterClearButtonSx : filterApplyButtonSx}
                      onClick={shouldShowClearFilters ? handleClearFilters : handleApplyFilters}
                    >
                      {filtersButtonLabel}
                    </Button>
                  </Box>
                </Box>
                {filterWarning ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {filterWarning}
                  </Alert>
                ) : null}
              </MDBox>

              <MDBox mx={2} pb={3} px={2}>
                {loading && <MDTypography>Cargando contabilidad...</MDTypography>}

                {!loading && errorMessage && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}

                {!loading && !errorMessage && (
                  <>
                    {hasNoMachines && (
                      <Box mb={3}>
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          No existen recaudaciones ingresadas.
                        </Alert>
                      </Box>
                    )}

                    {hasMachinesButNoRows && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        {isRangeMode
                          ? "No hay recaudaciones en el rango seleccionado."
                          : "Hay tragamonedas, pero no hay recaudaciones en las últimas 20."}
                      </Alert>
                    )}

                    {rows.length > 0 && (
                      <Box mb={4}>
                        {!isRangeMode && pagination?.hasMore && (
                          <Box display="flex" justifyContent="flex-end" mb={2}>
                            <Button
                              variant="outlined"
                              color="info"
                              onClick={handleLoadMore}
                              disabled={loadingMore}
                              sx={secondaryOutlinedButtonSx}
                            >
                              {loadingMore ? "Cargando..." : "Cargar más recaudaciones"}
                            </Button>
                          </Box>
                        )}
                        <SlotAccountingGrid
                          machines={machines}
                          rows={rows}
                          localId={localId}
                          activeLocalMachines={activeLocalMachines}
                          viewState={{
                            filters: {
                              ...appliedFilters,
                              includeInitialValues: true,
                            },
                            showDebugJson: canShowDebugJson ? showDebugJson : false,
                            slotAccountingSnapshot: {
                              data,
                              pagination,
                            },
                          }}
                          initialScroll={restoredViewState?.gridScroll || null}
                        />
                      </Box>
                    )}

                    {canShowDebugJson && (
                      <Box>
                        <Button
                          variant="text"
                          color="info"
                          onClick={() => setShowDebugJson((prev) => !prev)}
                          sx={debugTextButtonSx}
                        >
                          {showDebugJson ? "Ocultar JSON temporal" : "Ver JSON temporal"}
                        </Button>
                        <Collapse in={showDebugJson}>
                          <Box
                            mt={1}
                            p={2}
                            borderRadius="10px"
                            sx={{
                              backgroundColor: "#f7f9fc",
                              border: "1px solid rgba(0,0,0,0.08)",
                              overflowX: "auto",
                            }}
                          >
                            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {JSON.stringify(data, null, 2)}
                            </pre>
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default SlotAccounting;
